//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PinterestGatewayBoard } from '@bvengineering/pinterest-gateway-service-types';
import { ScheduledPostTimeUpdate } from '@bvengineering/universal-scheduling-gateway-service-types';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { PojoSet } from 'pojo-sets';
import { useDispatch, useSelector } from 'react-redux';

import {
  getLocationsFromPagesSearch,
  getPageById,
  getPagesSearch,
} from '@apis/gateway-instagram/facebookGraphApiSearchApi';
import { getOnlineFollowersHeatmap } from '@apis/gateway-instagram/instagramAudienceMetricsApi';
import { getTopHashtagRecommendations } from '@apis/gateway-instagram/instagramHashtagRecommendationsApi';
import { getLinkMetadata } from '@apis/gatewayFacebookApi';
import {
  getInstagramShopTagCatalogs,
  getInstagramShopTagEligibility,
} from '@apis/instagram-shops/instagramShopsApi';
import { getDefaultInstagramUserLivePosts } from '@apis/instagram-user/instagramUserApi';
import { getBoardsPaged } from '@apis/pinterest-user/pinterestUserApi';
import { getInstagramSchedulingEligibleUsers } from '@apis/publish/publishApi';
import { ScheduleInstagramPostErrorCode } from '@apis/publish/publishApiModels';
import { downloadPdfReport } from '@apis/screen-capture/screenCaptureApi';
import { SCREEN_CAPTURE_PAGES } from '@apis/screen-capture/screenCaptureApiModels';
import {
  getContextualShowroom,
  getShowroomShortLink,
} from '@apis/showroom/showroomApi';
import { getAudienceBestTimesToPost } from '@apis/tiktok-analytics/tiktokAnalyticsApi';
import { searchUser } from '@apis/twitter-search/twitterSearchApi';
import {
  ConnectionId,
  TConnectionConfig,
  UniversalConnectionType,
} from '@apis/universal-posts/connections/universalPostConnectionModel';
import { uploadCoverImage } from '@apis/universal-posts/cover-image/universalPostsCoverImageApi';
import {
  deleteDraftPosts,
  getDraftPosts,
  upsertDraftPosts,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import { IUpsertDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  deleteScheduledPosts,
  getScheduledPost,
  getScheduledPosts,
  reschedulePosts,
  searchScheduledPosts,
  upsertScheduledPosts,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import {
  ICreateUpdateScheduledPost,
  IScheduledPost,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { getSocialNetworkFromPostType } from '@apis/universal-posts/universalPostsUtils';
import {
  getAugmentedSwipeUpLink,
  getDestinationUrlFromShortlink,
  getShortlink,
  getTrackingShortLink,
} from '@apis/url-tracking/urlTrackingApi';
import { SourceType } from '@apis/url-tracking/urlTrackingApiModel';
import {
  getUserSettings,
  setUserSettings,
} from '@apis/user-settings/userSettingsApi';
import {
  createYouTubePlaylist,
  getYouTubePlaylists,
  getYouTubeUser,
  getYouTubeVideoCategories,
} from '@apis/youtube/youtubeApi';
import {
  CreateYouTubePlaylistRequest,
  YouTubePlaylist,
  YouTubeVideoCategory,
} from '@apis/youtube/youtubeApiModel';
import {
  IFacebookPageSuggestion,
  ITwitterUserSuggestion,
  MentionsSuggestionType,
} from '@components/molecules/smart-input/smartInputModel';
import { SHOPPABLE_INSTAGRAM_TYPES } from '@instagram-account/instagram-account-constants/instagram-account-constants';
import { IInstagramPostDefaults } from '@instagram-account/redux/post-details/defaults/instagramPostDetailsDefaultsModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { IShowroomData } from '@legacy-components/common/universal-image-actions/models/events';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import DraftId from '@models/DraftId';
import FacebookPageId from '@models/FacebookPageId';
import { IFacebookPage } from '@models/IFacebookPage';
import { LegacyInstagramPublishingStrategy } from '@models/IUserSettings';
import { InstagramPublishingStrategy } from '@models/InstagramPublishingStrategy';
import PostableId from '@models/PostableId';
import { SocialNetwork } from '@models/SocialNetwork';
import TikTokUserId from '@models/TikTokUserId';
import TimeZoneId from '@models/TimeZoneId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import {
  FIVE_MINUTES_STALE_TIME,
  TWENTY_SECONDS_STALE_TIME,
} from '@queries/QueryOptionsConstants';
import { queryClient } from '@queries/ReactQueryProvider';
import { getUserFullName } from '@redux/selectors/getUserFullName';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import {
  FEED_PREVIEW_LIVE_POST_LIMIT,
  FILENAME_DATE_FORMAT,
} from '@universal-scheduling/components/calendar/feed-preview/scheduleFeedPreviewConstants';
import {
  getFeedPreviewSearchStartDate,
  getTemporaryScheduledPostsForBatchReschedule,
  instagramFeedPreviewScheduledPostingGroupNames,
} from '@universal-scheduling/components/calendar/feed-preview/scheduleFeedPreviewUtils';
import { usePostDetailsFormPostTypesAndFormIds } from '@universal-scheduling/components/scheduler/hooks/useSchedulerPostTypesFromTabData';
import {
  FACEBOOK_PAGE_SEARCH_COUNT,
  FACEBOOK_VERIFIED_PAGE_STATUS,
} from '@universal-scheduling/components/scheduler/post-details-forms/facebook/FacebookPostDetailsConstants';
import { TOP_INSTAGRAM_HASHTAG_RECOMMENDATIONS_LIMIT } from '@universal-scheduling/components/scheduler/post-details-forms/instagram/InstagramPostDetailsConstants';
import { MAX_BOARD_PAGES } from '@universal-scheduling/components/scheduler/post-details-forms/pinterest/PinterestPostDetailsConstants';
import { POST_DETAILS_INVALID_TAGGED_USER_TEXT } from '@universal-scheduling/components/scheduler/post-details-forms/postDetailsFormConstants';
import {
  getTop3TimesToPost,
  rotateHourlyHeatmapToTimezone,
} from '@universal-scheduling/components/scheduler/post-details-forms/postDetailsFormUtils';
import { BestTimesToPostByDay } from '@universal-scheduling/components/scheduler/post-details-forms/shared/recommended-times/RecommendedTimesConstants';
import { TWITTER_POST_DETAILS_SMART_INPUT_USER_SEARCH_COUNT } from '@universal-scheduling/components/scheduler/post-details-forms/twitter/TwitterPostDetailsConstants';
import { SCHEDULER_DEFAULT_ERROR_TEXT } from '@universal-scheduling/components/scheduler/schedulerConstants';
import { getSchedulerMediaErrorMessage } from '@universal-scheduling/components/scheduler/top-level-fields/shared-media/schedulerSharedMediaValidationUIConstants';
import { SchedulerMediaError } from '@universal-scheduling/components/scheduler/top-level-fields/shared-media/schedulerSharedMediaValidationUtils';
import { usePostDetailsFormContext } from '@universal-scheduling/contexts/scheduler/PostDetailsFormContext';
import { useSchedulerContext } from '@universal-scheduling/contexts/scheduler/SchedulerContext';
import { SchedulerKeyGroupings } from '@universal-scheduling/queries/universalSchedulerQueryKeyConstants';
import { getCurrentDateRange } from '@universal-scheduling/redux/calendar/selectors/getUniversalCalendarSettings';
import {
  NormalizedScheduledPosts,
  scheduledPostStatuses,
} from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { fetchPosts } from '@universal-scheduling/redux/calendar/universalCalendarSlice';
import { uniq } from '@utils/arrayUtils';
import { isDefined, isTruthy, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorCode } from '@utils/getRequestErrorCode';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { mapObject } from '@utils/mapObject';
import { DAYS_OF_WEEK, DAYS_OF_WEEK_ABBR } from '@utils/moment';
import normalize from '@utils/normalize';
import { sortBy } from '@utils/sortBy';
import { isCuralateShortLink } from '@utils/urlValidation';

export function useFacebookCurrentLinkMetadataQuery(postUrl: string) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetFacebookLinkMetadata,
      postUrl,
    ],
    queryFn: async () => {
      // Get metadata for original link if possible
      const url = await (async () => {
        if (isCuralateShortLink(postUrl)) {
          return (
            (await getDestinationUrlFromShortlink({
              shortlink: postUrl,
            })) ?? postUrl
          );
        }
        return postUrl;
      })();

      return await getLinkMetadata(url);
    },
  });
}

export function useScheduledPostInfoQuery(postId: PostableId | undefined) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetPost,
      postId,
    ],
    queryFn: async () => {
      if (!isDefined(postId)) {
        return Promise.reject(new Error('postId was undefined'));
      }
      return await getScheduledPost(postId);
    },
    enabled: isDefined(postId),
  });
}

export function useScheduledPostInfoWithConnectionsQuery(
  postId: PostableId | undefined,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetPost,
      postId,
    ],
    queryFn: async () => {
      if (!isDefined(postId)) {
        return Promise.reject(new Error('postId was undefined'));
      }
      const basePost = await getScheduledPost(postId);
      const batchCreateConnections =
        basePost.connections?.filter(
          (connection) =>
            connection.connectionType === UniversalConnectionType.BatchCreate,
        ) ?? [];
      const connectedPostIds = PojoSet.toArray(
        PojoSet.from(
          batchCreateConnections
            .flatMap((connection) => connection.connectedPostableIds)
            .filter((id) => id !== postId),
        ),
      );
      const connectedPosts = await getScheduledPosts(connectedPostIds);
      return {
        connections: batchCreateConnections,
        posts: [basePost, ...connectedPosts],
      };
    },
    enabled: isDefined(postId),
  });
}

export function useDraftPostInfoQuery(draftId: DraftId | undefined) {
  const { data, ...result } = useGetDraftPostsQuery(
    isDefined(draftId) ? [draftId] : [],
  );
  return { data: data?.[0], ...result };
}

export function useGetDraftPostsQuery(draftIds: DraftId[]) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetDrafts,
      draftIds,
    ],
    queryFn: async () => {
      return await getDraftPosts(draftIds);
    },
    enabled: draftIds.length > 0,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useDraftPostInfoWithConnectionsQuery(
  draftId: DraftId | undefined,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetDraft,
      draftId,
    ],
    queryFn: async () => {
      if (!isDefined(draftId)) {
        return Promise.reject(new Error('draftId was undefined'));
      }
      const baseDrafts = await getDraftPosts([draftId]);
      const batchCreateConnections = baseDrafts.flatMap(
        (draft) =>
          draft.connections?.filter(
            (connection) =>
              connection.connectionType === UniversalConnectionType.BatchCreate,
          ) ?? [],
      );
      const connectedDraftIds = PojoSet.toArray(
        PojoSet.from(
          batchCreateConnections
            .flatMap((connection) => connection.connectedDraftIds)
            .filter((id) => id !== draftId),
        ),
      );
      const connectedPosts = await getDraftPosts(connectedDraftIds);
      return {
        connections: batchCreateConnections,
        drafts: [...baseDrafts, ...connectedPosts],
      };
    },
    enabled: isDefined(draftId),
  });
}

export function useGetConnectionPosts(
  postIds: PostableId[],
  draftIds: DraftId[],
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetConnectionPosts,
      postIds,
      draftIds,
    ],
    queryFn: async () => {
      const draftsFut = getDraftPosts(draftIds);
      const postsFut = getScheduledPosts(postIds);
      const drafts = await draftsFut;
      const posts = await postsFut;
      return [...drafts, ...posts];
    },
  });
}

export function useFetchEligibleUsersQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetEligibleUsers,
    ],
    queryFn: ({ signal }) => getInstagramSchedulingEligibleUsers({ signal }),
    staleTime: TWENTY_SECONDS_STALE_TIME,
  });
}

export function useFetchCurrentDefaultsQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetCurrentDefaults,
    ],
    queryFn: async ({ signal }) => {
      const {
        defaultNotificationUserId,
        defaultPostingTime: defaultPostTime,
        defaultPublishingStrategy: legacyDefaultPublishingStrategy,
      } = await getUserSettings({ signal });

      // The publishing strategy user settings are stored using an old enum, so map them
      // to the current enum for use in the post details infrastructure.
      const defaultPublishingStrategy =
        legacyDefaultPublishingStrategy &&
        (legacyDefaultPublishingStrategy ===
        LegacyInstagramPublishingStrategy.Automatic
          ? InstagramPublishingStrategy.Automatic
          : InstagramPublishingStrategy.Manual);

      return {
        defaultNotificationUserId: defaultNotificationUserId?.userId,
        defaultPublishingStrategy,
        defaultPostTime: isDefined(defaultPostTime?.hour)
          ? defaultPostTime
          : undefined,
      };
    },
    staleTime: TWENTY_SECONDS_STALE_TIME,
  });
}

export function useUpdateSchedulerDefaultsMutation() {
  return useMutation({
    mutationFn: async ({
      defaultNotificationUserId,
      defaultPostTime,
      defaultPublishingStrategy,
    }: IInstagramPostDefaults) => {
      const publishingStrategy =
        defaultPublishingStrategy === InstagramPublishingStrategy.Automatic
          ? LegacyInstagramPublishingStrategy.Automatic
          : LegacyInstagramPublishingStrategy.Manual;

      await setUserSettings({
        defaultNotificationUserId: {
          userId: defaultNotificationUserId,
        },
        defaultPostingTime: defaultPostTime,
        defaultPublishingStrategy: publishingStrategy,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([
        QueryKeyConstants.Scheduling,
        QueryKeyConstants.SchedulingGetCurrentDefaults,
        QueryKeyConstants.SchedulingGetEligibleUsers,
      ]);
    },
  });
}

export const getScheduledPostsById = (
  queryClient: QueryClient,
  postableIds: PostableId[],
): Promise<IScheduledPost[]> => {
  if (postableIds.length === 0) {
    return Promise.resolve([]);
  }

  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetPost,
      postableIds,
    ],
    queryFn: async () => {
      return await getScheduledPosts(postableIds);
    },
  });
};

export const getTwitterUserSearch = (
  queryClient: QueryClient,
  query: string,
): Promise<ITwitterUserSuggestion[]> => {
  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetTwitterUserSearch,
      query,
    ],
    queryFn: async () => {
      const searchResults = await searchUser({
        query,
        count: TWITTER_POST_DETAILS_SMART_INPUT_USER_SEARCH_COUNT,
      });

      const modifiedSearchResults = searchResults
        .map((user) => {
          const twitterUserSuggestion: ITwitterUserSuggestion = {
            avatarUrl: user.profileImageUrl?.normal ?? '',
            isVerified: user.verified,
            name: user.name ?? '',
            type: MentionsSuggestionType.TwitterUser,
            username: user.screenName ?? '',
          };
          return twitterUserSuggestion;
        })
        .filter((suggestion) => {
          return suggestion.avatarUrl && suggestion.name && suggestion.username;
        });

      return modifiedSearchResults;
    },
  });
};

export const getSmartInputDestinationUrlFromShortLink = (
  queryClient: QueryClient,
  shortLink: string,
): Promise<string | undefined> => {
  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetDestinationUrlFromShortLink,
      shortLink,
    ],
    queryFn: async () => {
      const originalLink = await getDestinationUrlFromShortlink({
        shortlink: shortLink,
      });
      return originalLink;
    },
  });
};

export const getSmartInputShortLink = (
  queryClient: QueryClient,
  url: string,
  sourceType: SourceType,
): Promise<string> => {
  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetShortLink,
      url,
      sourceType,
    ],
    queryFn: async () => {
      const shortLink = await getShortlink({
        url,
        sourceType,
      });
      return shortLink;
    },
  });
};

export const getSmartInputTrackingShortLink = (
  queryClient: QueryClient,
  url: string,
  sourceType: SourceType,
): Promise<string> => {
  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetTrackingShortLink,
      url,
      sourceType,
    ],
    queryFn: async () => {
      const shortLink = await getTrackingShortLink({
        url,
        sourceType,
      });
      return shortLink;
    },
  });
};

export function useInstagramLocationSearchQuery(locationSearchText: string) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingInstagramLocationSearch,
      locationSearchText,
    ],
    queryFn: async ({ signal }) => {
      const searchResults = await getLocationsFromPagesSearch(
        locationSearchText,
        undefined,
        signal,
      );

      return searchResults.locations;
    },
    enabled: isTruthy(locationSearchText),
  });
}

const buildFacebookPageSuggestion = (
  page: IFacebookPage,
): IFacebookPageSuggestion => {
  return {
    id: FacebookPageId(page.id),
    isVerified: page.verificationStatus === FACEBOOK_VERIFIED_PAGE_STATUS,
    link: page.link ?? '',
    name: page.name ?? '',
    type: MentionsSuggestionType.FacebookPage,
  };
};

export const searchSmartInputFacebookPages = (
  queryClient: QueryClient,
  query: string,
): Promise<IFacebookPageSuggestion[]> => {
  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingSearchFacebookPages,
      query,
    ],
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const response = await getPagesSearch(query, FACEBOOK_PAGE_SEARCH_COUNT, {
        signal,
      });
      return response
        .map((page) => {
          return buildFacebookPageSuggestion(page);
        })
        .filter((suggestion) => suggestion.name);
    },
  });
};

export const getSmartInputFacebookPageById = (
  queryClient: QueryClient,
  pageId: FacebookPageId,
): Promise<IFacebookPageSuggestion> => {
  return queryClient.fetchQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetFacebookPageById,
      pageId,
    ],
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const response = await getPageById(pageId, { signal });
      return buildFacebookPageSuggestion(response);
    },
  });
};

const fetchAllBoards = async (
  page: number,
  bookmark?: string | undefined,
): Promise<PinterestGatewayBoard[]> => {
  try {
    const { boards, bookmark: nextBookmark } = await getBoardsPaged(bookmark);

    if (!!nextBookmark && boards.length > 0 && page < MAX_BOARD_PAGES) {
      const nextBoards = await fetchAllBoards(page + 1, nextBookmark);
      return [...boards, ...nextBoards];
    }

    return boards;
  } catch {
    return [];
  }
};

export const useGetPinterestUserBoards = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetPinterestUserBoards,
    ],
    queryFn: async () => {
      const boards = await fetchAllBoards(0);

      boards.sort((a, b) =>
        (a.name?.toLowerCase() ?? '') < (b.name?.toLowerCase() ?? '') ? -1 : 1,
      );
      return boards;
    },
  });
};

export function useGetInstagramRecommendedHashtagsQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetInstagramRecommendedHashtags,
    ],
    queryFn: async () => {
      const recommendedHashtags = await getTopHashtagRecommendations(
        TOP_INSTAGRAM_HASHTAG_RECOMMENDATIONS_LIMIT,
      );
      return recommendedHashtags;
    },
  });
}

export function useInstagramShopsCatalogsQuery(
  isEligible: boolean | undefined,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetInstagramShopsCatalogs,
      isEligible,
    ],
    queryFn: async () => {
      return await getInstagramShopTagCatalogs();
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: !!isEligible,
  });
}

export function useInstagramShopsEligibilityQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetInstagramShopsEligibility,
    ],
    queryFn: async () => {
      return await getInstagramShopTagEligibility();
    },
    staleTime: Infinity,
  });
}

export function useGetFullUrlFromShortlinkQuery(url: string, isValid: boolean) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetDestinationUrlFromShortLink,
      url,
      isValid,
    ],
    queryFn: async () => {
      if (!isValid) {
        return Promise.reject(new Error('invalid query'));
      }
      const fullUrl = await getDestinationUrlFromShortlink({
        shortlink: url,
      });
      return fullUrl;
    },
    staleTime: Infinity,
    enabled: isValid,
  });
}

export function useFeedPreviewLiveInstagramPostsQuery(isEnabled: boolean) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetFeedPreviewLiveInstagramPosts,
    ],
    queryFn: async () => {
      const { items: livePosts } = await getDefaultInstagramUserLivePosts({
        limit: FEED_PREVIEW_LIVE_POST_LIMIT,
      });
      const normalizedLivePosts = normalize(livePosts, (post) => post.itemId);

      return normalizedLivePosts;
    },
    enabled: isEnabled,
    staleTime: TWENTY_SECONDS_STALE_TIME,
  });
}

export function useFeedPreviewScheduledInstagramPostsQuery(isEnabled: boolean) {
  const timezone = useSelector(getUserTimeZone);
  const earliestDate = getFeedPreviewSearchStartDate(timezone);

  return useQuery({
    queryKey: [
      ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
      earliestDate,
    ],
    queryFn: async () => {
      const { data: scheduledPosts } = await searchScheduledPosts({
        status: scheduledPostStatuses,
        groups: instagramFeedPreviewScheduledPostingGroupNames,
        after: earliestDate,
      });

      const normalizedScheduledPosts = normalize(
        sortBy(scheduledPosts, 'scheduledTime'),
        (post) => post.id,
      );

      return normalizedScheduledPosts;
    },
    enabled: isEnabled,
    staleTime: TWENTY_SECONDS_STALE_TIME,
  });
}

interface BatchUpdateScheduledPostRequest {
  scheduledPostTimeUpdates: ScheduledPostTimeUpdate[];
}

export const useBatchUpdateScheduledPostTimeMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const dateRange = useSelector(getCurrentDateRange);
  const timezone = useSelector(getUserTimeZone);
  const earliestDate = getFeedPreviewSearchStartDate(timezone);

  return useMutation({
    mutationFn: async ({
      scheduledPostTimeUpdates,
    }: BatchUpdateScheduledPostRequest) => {
      return reschedulePosts(scheduledPostTimeUpdates);
    },
    onMutate: ({ scheduledPostTimeUpdates }) => {
      queryClient.cancelQueries([
        ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
        earliestDate,
      ]);

      const currentScheduledPosts: NormalizedScheduledPosts | undefined =
        queryClient.getQueryData([
          ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
          earliestDate,
        ]);

      if (isDefined(currentScheduledPosts)) {
        const temporaryScheduledPosts =
          getTemporaryScheduledPostsForBatchReschedule(
            currentScheduledPosts,
            scheduledPostTimeUpdates,
          );

        queryClient.setQueryData(
          [
            ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
            earliestDate,
          ],
          temporaryScheduledPosts,
        );
      }
      return currentScheduledPosts;
    },
    onSuccess: (_data, { scheduledPostTimeUpdates }) => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Rescheduled',
          type: ToastType.SUCCESS,
          message: `You have rescheduled ${scheduledPostTimeUpdates.length} posts.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onError: (_error, _variables, currentScheduledPosts) => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Unable to Reschedule',
          type: ToastType.ERROR,
          message: `We were unable to reschedule your posts. Please try again or contact Bazaarvoice Support.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );

      queryClient.setQueryData(
        [
          ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
          earliestDate,
        ],
        currentScheduledPosts,
      );
    },
    onSettled: (_data, _error, { scheduledPostTimeUpdates }) => {
      void queryClient.invalidateQueries([
        ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
        earliestDate,
      ]);

      const shouldRefetchCalendar = scheduledPostTimeUpdates.some((post) => {
        const firstDateInRange = DateString.toDate(
          dateRange.startDate,
          timezone,
        ).getTime();
        const lastDateInRange = DateString.toDate(
          dateRange.endDate,
          timezone,
        ).getTime();

        return (
          post.scheduledTime >= firstDateInRange &&
          post.scheduledTime <= lastDateInRange
        );
      });

      if (shouldRefetchCalendar) {
        // dispatch to fetch scheduled posts again
        void dispatch(fetchPosts(true));
      }
    },
  });
};

interface ExportInstagramFeedPreviewRequest {
  latestPostDate: Date;
  username: string;
}

export const useExportInstagramFeedPreviewMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fullName = useSelector(getUserFullName);
  const timezone = useSelector(getUserTimeZone);
  const earliestDate = getFeedPreviewSearchStartDate(timezone);

  return useMutation({
    mutationFn: async ({
      latestPostDate,
      username,
    }: ExportInstagramFeedPreviewRequest) => {
      const formattedDate = DateString.format(
        DateString.fromTimestamp(latestPostDate, timezone),
        FILENAME_DATE_FORMAT,
      );

      await downloadPdfReport({
        pageName: SCREEN_CAPTURE_PAGES.INSTAGRAM_FEED_PREVIEW,
        creatorName: fullName,
        filename: `feedpreview_${formattedDate}_${username}`,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries([
        ...SchedulerKeyGroupings.SchedulingFeedPreviewScheduledInstagramPosts,
        earliestDate,
      ]);

      // if user doesn't close the toast it will remain until the download is completed
      dispatch(
        toastNotificationsActions.addToastNotification({
          id: 'downloading-pdf',
          title: 'Downloading PDF',
          type: ToastType.INFO,
          message:
            "We're exporting your feed preview PDF. It will download automatically when it's ready.",
          options: {
            extendedTimeOut: 0,
            hideProgress: false,
            timeOut: 0,
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.closeToastNotification('downloading-pdf'),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Download Failed',
          type: ToastType.ERROR,
          message:
            'Your feed preview PDF failed to download. Please try again or contact Bazaarvoice Support.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.closeToastNotification('downloading-pdf'),
      );
    },
  });
};

// Saving Drafts / Scheduling Posts
interface UpsertDraftParams {
  draftPosts: IUpsertDraftPost[];
  connectionId?: ConnectionId;
  connectionConfiguration?: TConnectionConfig;
}

export const useUpsertDraftPostsMutation = () => {
  const queryClient = useQueryClient();
  const mutationKey = SchedulerKeyGroupings.SchedulingUpsertDraftsMutation;

  return useMutation({
    mutationKey,
    mutationFn: ({
      connectionConfiguration,
      connectionId,
      draftPosts,
    }: UpsertDraftParams) => {
      return upsertDraftPosts(
        draftPosts,
        connectionId,
        connectionConfiguration,
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([QueryKeyConstants.Scheduling]);
    },
  });
};

interface UpsertScheduledPostParams {
  scheduledPosts: ICreateUpdateScheduledPost[];
  connectionId?: ConnectionId;
  connectionConfiguration?: TConnectionConfig;
}

export const useUpsertScheduledPostsMutation = () => {
  const queryClient = useQueryClient();
  const mutationKey =
    SchedulerKeyGroupings.SchedulingUpsertScheduledPostsMutation;

  const { setSharedState } = useSchedulerContext();

  const postTypesAndFormIds = usePostDetailsFormPostTypesAndFormIds();
  const instagramPostTypeAndFormId = postTypesAndFormIds.find(
    ({ postType }) =>
      getSocialNetworkFromPostType(postType) === SocialNetwork.Instagram,
  );
  const { getFieldPath, setError } = usePostDetailsFormContext(
    instagramPostTypeAndFormId?.formId ?? '',
    SocialNetwork.Instagram,
  );
  const postType = instagramPostTypeAndFormId?.postType;

  return useMutation({
    mutationKey,
    mutationFn: ({
      connectionConfiguration,
      connectionId,
      scheduledPosts,
    }: UpsertScheduledPostParams) => {
      return upsertScheduledPosts(
        scheduledPosts,
        connectionId,
        connectionConfiguration,
      );
    },
    onError: (error) => {
      if (isDefined(postType)) {
        const errorCode = getRequestErrorCode(error);
        const errorMessage = getRequestErrorMessage(error);

        if (
          errorCode ===
          ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_INVALID_TAGGED_USER
        ) {
          setError(getFieldPath('userTags'), {
            message: POST_DETAILS_INVALID_TAGGED_USER_TEXT,
            type: `${errorCode}`,
          });
          return;
        }

        const schedulerErrorMessage = (() => {
          if (
            errorCode ===
            ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_UNSUPPORTED_ASPECT_RATIO
          ) {
            return getSchedulerMediaErrorMessage({
              cuids: [],
              error: SchedulerMediaError.InvalidImageAspectRatio,
              postType,
            });
          } else if (
            errorCode ===
            ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_IMAGE_SIZE_TOO_LARGE
          ) {
            return getSchedulerMediaErrorMessage({
              cuids: [],
              error: SchedulerMediaError.ImageFileTooLarge,
              postType,
            });
          } else if (isDefined(errorMessage)) {
            return errorMessage;
          } else {
            return SCHEDULER_DEFAULT_ERROR_TEXT;
          }
        })();

        setSharedState((prev) => {
          return {
            ...prev,
            scheduleTimeFailureMessages: [schedulerErrorMessage],
          };
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKeyConstants.Scheduling]);
    },
  });
};

export const useDeleteDraftPostsMutation = () => {
  const queryClient = useQueryClient();
  const mutationKey = SchedulerKeyGroupings.SchedulingDeleteDraftsMutation;

  return useMutation({
    mutationKey,
    mutationFn: (draftIds: DraftId[]) => {
      return deleteDraftPosts(draftIds);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([QueryKeyConstants.Scheduling]);
    },
  });
};

export const useDeleteScheduledPostsMutation = () => {
  const queryClient = useQueryClient();
  const mutationKey =
    SchedulerKeyGroupings.SchedulingDeleteScheduledPostsMutation;

  return useMutation({
    mutationKey,
    mutationFn: (postableIds: PostableId[]) => {
      return deleteScheduledPosts(postableIds);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([QueryKeyConstants.Scheduling]);
    },
  });
};

export const useGetInstagramBestTimesToPost = (
  isEnabled: boolean,
  timeZone: TimeZoneId,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetInstagramBestTimesToPost,
      timeZone,
    ],
    queryFn: async (): Promise<BestTimesToPostByDay> => {
      const { onlineFollowers, timeZone: heatmapTimeZone } =
        await getOnlineFollowersHeatmap();

      if (isUndefined(onlineFollowers)) {
        return {};
      }

      const timeZonedHeatMap = rotateHourlyHeatmapToTimezone(
        onlineFollowers,
        heatmapTimeZone,
        timeZone,
      );

      const bestTimesToPostByDay: BestTimesToPostByDay = mapObject(
        timeZonedHeatMap,
        getTop3TimesToPost,
      );

      return bestTimesToPostByDay;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isEnabled,
  });
};

export const useGetTikTokBestTimesToPost = (
  isEnabled: boolean,
  timeZone: TimeZoneId,
  tiktokUserId: TikTokUserId | undefined,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetTikTokBestTimesToPost,
      timeZone,
      tiktokUserId,
    ],
    queryFn: async () => {
      if (!isDefined(tiktokUserId)) {
        throw new Error(
          'useGetTikTokBestTimesToPost: tiktokUserId is required',
        );
      }

      const bestTimesToPost = await getAudienceBestTimesToPost({
        tiktokUserId,
        timeZone,
      });

      if (DAYS_OF_WEEK.some((day) => !bestTimesToPost.bestTimes?.[day])) {
        return {};
      }

      const bestTimesToPostByDay: BestTimesToPostByDay = Object.fromEntries(
        DAYS_OF_WEEK_ABBR.map((dayOfWeekAbbr, index) => {
          const timeData =
            bestTimesToPost.bestTimes?.[DAYS_OF_WEEK[index] ?? 'sunday'];
          return [
            dayOfWeekAbbr,
            timeData?.map((value) => ({
              rank: value.rank,
              reach: value.followerCount,
              hourOfDay: value.hourOfDay,
            })),
          ];
        }),
      );

      return bestTimesToPostByDay;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isDefined(tiktokUserId) && isEnabled,
  });
};

export function useContextualShowroomLinkQuery(
  cuid: Cuid | undefined,
  url: string | undefined,
  isValid: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetContextualShowroomLink,
      cuid,
      url,
      isValid,
    ],
    queryFn: async () => {
      if (!isDefined(cuid)) {
        return Promise.reject(new Error('cuid was undefined'));
      }
      if (!isDefined(url)) {
        return Promise.reject(new Error('url was undefined'));
      }
      if (!isValid) {
        return Promise.reject(new Error('link was invalid'));
      }

      const { previewUrl, shortLink } = await getContextualShowroom({
        sourceId: cuid,
        implementationType: SHOPPABLE_INSTAGRAM_TYPES.IG_STORY,
        showroomUrl: url,
      });

      return { shortLink, previewUrl };
    },
    enabled: isValid && isDefined(cuid) && isTruthy(url),
    staleTime: Infinity,
  });
}

export function useAugmentedSwipeUpLinkQuery(
  cuid: Cuid | undefined,
  url: string | undefined,
  shouldFetch: boolean,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetAugmentedSwipeUpLink,
      cuid,
      url,
      shouldFetch,
    ],
    queryFn: async () => {
      if (isUndefined(cuid)) {
        return Promise.reject(new Error('cuid was undefined'));
      }
      if (isUndefined(url)) {
        return Promise.reject(new Error('url was undefined'));
      }

      const link = await getAugmentedSwipeUpLink({
        url,
        cuid,
      });

      return link;
    },
    enabled: shouldFetch && isDefined(cuid),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
}

export function useShowroomShortLinkQuery(
  showroomData: IShowroomData | undefined,
  sourceId: string | undefined,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingGetShowroomShortLink,
      showroomData,
      sourceId,
    ],
    queryFn: async () => {
      if (!isDefined(showroomData)) {
        return Promise.reject(new Error('showroomData was undefined'));
      }
      if (!isDefined(sourceId)) {
        return Promise.reject(new Error('sourceId was undefined'));
      }

      const { shortLink } = await getShowroomShortLink({
        implId: showroomData.impl.id,
        name: showroomData.impl.name,
        sourceId,
        sourceType: 'item',
        instanceId: showroomData.instances?.[0]?.instanceId,
      });

      return shortLink;
    },
    enabled: isDefined(showroomData) && isDefined(sourceId),
    staleTime: Infinity,
  });
}

export function useGetYouTubePlaylistsQuery<TData = YouTubePlaylist[]>(
  select?: (data: YouTubePlaylist[]) => TData,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingYouTube,
      QueryKeyConstants.SchedulingGetYouTubePlaylists,
    ],
    queryFn: async ({ signal }) => {
      const getAllYouTubePlaylists = async (
        init?: RequestInit,
        pageToken: string = '',
      ): Promise<YouTubePlaylist[]> => {
        const {
          pagination: { nextPageToken },
          playlists,
        } = await getYouTubePlaylists({ pageToken }, init);
        if (isDefined(nextPageToken)) {
          return playlists.concat(
            await getAllYouTubePlaylists(init, nextPageToken),
          );
        }
        return playlists;
      };

      return getAllYouTubePlaylists({ signal });
    },
    select,
    staleTime: Infinity,
  });
}

export function useGetYouTubeCategoriesQuery<TData = YouTubeVideoCategory[]>(
  select?: (data: YouTubeVideoCategory[]) => TData,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingYouTube,
      QueryKeyConstants.SchedulingGetYouTubeVideoCategories,
    ],
    queryFn: async ({ signal }) => {
      return getYouTubeVideoCategories({ signal });
    },
    select,
    staleTime: Infinity,
  });
}

// TODO: PD-264381: Remove isEnabled after cleanup youtube_automatic_posting feature flag
export const useGetYouTubeUserQuery = (isEnabled: boolean) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.Scheduling,
      QueryKeyConstants.SchedulingYouTube,
      QueryKeyConstants.SchedulingGetYouTubeUser,
    ],
    queryFn: async ({ signal }) => {
      return await getYouTubeUser({ signal });
    },
    enabled: isEnabled,
    staleTime: Infinity,
  });
};

export const useCreateYouTubePlaylistMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const creatingPlaylistToastId = 'creating-youtube-playlist';

  return useMutation({
    mutationFn: async ({
      description,
      privacy,
      title,
    }: CreateYouTubePlaylistRequest) => {
      return await createYouTubePlaylist({
        description,
        privacy,
        title,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries([
        QueryKeyConstants.Scheduling,
        QueryKeyConstants.SchedulingYouTube,
        QueryKeyConstants.SchedulingGetYouTubePlaylists,
      ]);

      dispatch(
        toastNotificationsActions.addToastNotification({
          id: creatingPlaylistToastId,
          title: 'Creating Playlist',
          type: ToastType.INFO,
          message: "We're creating your YouTube playlist",
          options: {
            extendedTimeOut: 0,
            hideProgress: false,
            timeOut: 0,
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.closeToastNotification(
          creatingPlaylistToastId,
        ),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Playlist Creation Failed',
          type: ToastType.ERROR,
          message:
            'Creating your YouTube playlist failed. Please try again or contact Bazaarvoice Support.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSuccess: async (playlist) => {
      dispatch(
        toastNotificationsActions.closeToastNotification(
          creatingPlaylistToastId,
        ),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Playlist Created',
          type: ToastType.SUCCESS,
          message: 'Your YouTube playlist has been created.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );

      await queryClient.invalidateQueries([
        QueryKeyConstants.Scheduling,
        QueryKeyConstants.SchedulingYouTube,
        QueryKeyConstants.SchedulingGetYouTubePlaylists,
      ]);

      // YouTube doesn't always return the newly created playlist in time when we refetch the YouTube playlists
      // so we manually add the created playlist to the get YouTube playlists query data here
      queryClient.setQueryData<YouTubePlaylist[]>(
        [
          QueryKeyConstants.Scheduling,
          QueryKeyConstants.SchedulingYouTube,
          QueryKeyConstants.SchedulingGetYouTubePlaylists,
        ],
        (oldPlaylists) => uniq([...(oldPlaylists ?? []), playlist]),
      );
    },
  });
};

export const useUploadCoverImage = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload: File) => {
      return uploadCoverImage(payload);
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Cover image uploaded successfully!',
          message: "You've successfully uploaded this cover image.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Cover image upload failed',
          message:
            'Cover image couldn’t be uploaded. Please check your selected photo and try again.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
};
